import { useCallback, useEffect, useRef, useState } from 'react';
import type { LatLng } from '@lib/utils/coords';

/** Why the browser gave no position: refused, no fix, too slow, or no geolocation here (an old browser, plain http). */
export type GeoFailure = 'denied' | 'unavailable' | 'timeout' | 'unsupported';

const CODE: Record<number, GeoFailure> = { 1: 'denied', 2: 'unavailable', 3: 'timeout' };

/**
 * One position from the browser on request (`locate`), never watched. `busy` while asking; `failure` says why the
 * last request gave nothing (cleared by the next one). An answer that arrives after unmount, or after a newer request,
 * is dropped.
 */
export function useGeolocation(opts: { timeoutMs: number; maxAgeMs: number }) {
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<GeoFailure | null>(null);
  const run = useRef(0);
  useEffect(
    () => () => {
      run.current++;
    },
    [],
  );
  const { timeoutMs, maxAgeMs } = opts;
  const locate = useCallback(
    (onPosition: (p: LatLng & { accuracy: number }) => void) => {
      const id = ++run.current;
      setFailure(null);
      if (!('geolocation' in navigator) || !window.isSecureContext) return setFailure('unsupported');
      setBusy(true);
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          if (id !== run.current) return;
          setBusy(false);
          onPosition({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy });
        },
        (err) => {
          if (id !== run.current) return;
          setBusy(false);
          setFailure(CODE[err.code] ?? 'unavailable');
        },
        { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: maxAgeMs },
      );
    },
    [timeoutMs, maxAgeMs],
  );
  return { locate, busy, failure, clear: () => setFailure(null) };
}
